import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  homeTaskCards,
  homeEmptyStates,
  integrationParameters,
  leadEntities,
  members,
  operationAreas,
  photoSessions,
  posReportSections,
  priceActivities,
  properties,
  propertyEditOptions,
  propertyPriceBands,
  propertyTabs,
  registrationFiles,
  registrationSuccessSteps,
  registrationSteps,
  draftRows,
  rentOffers,
  reportMetrics,
  salePaymentMethods,
  saleOffers,
  similarTransactions,
  statusReasonGroups,
  statusRows,
  statusTabs,
  visits,
} from '../data';
import { money, propertySectionFromPath, statusFromPath, titleForPath } from '../formatters';
import { colors, styles } from '../styles';
import {
  Alert,
  ButtonLink,
  Card,
  CurrencyField,
  DataTable,
  DetailList,
  Drawer,
  FeedbackBlock,
  Field,
  Modal,
  PageIntro,
  PriceTable,
  PropertyCell,
  SearchBox,
  Section,
  Tabs,
  Tag,
  TaskCard,
  Timeline,
  UploadList,
} from '../ui';

const propertyById = (id = '1001') =>
  properties.find((property) => property.id === String(id)) || properties[0];

const extractSearchTerm = (pathname, params = {}) =>
  params.term || decodeURIComponent(pathname.split('/').filter(Boolean)[1] || 'A1001');

const columnsForNextOffers = [
  {
    key: 'address',
    label: 'Imóvel',
    render: (row) => <PropertyCell property={propertyById(row.propertyId)} />,
  },
  {
    key: 'offersCount',
    label: 'Propostas',
    render: (row) => <strong>{row.offersCount}</strong>,
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (
      <div>
        <Tag>{row.status}</Tag>
        <p style={{ ...styles.caption, marginTop: 6 }}>{row.updatedAt}</p>
      </div>
    ),
  },
];

const columnsForVisits = [
  {
    key: 'address',
    label: 'Imóvel',
    render: (row) => <PropertyCell property={propertyById(row.propertyId)} />,
  },
  { key: 'status', label: 'Status', render: (row) => <Tag>{row.status}</Tag> },
  { key: 'businessContext', label: 'Anúncio' },
  { key: 'authorization', label: 'Entrada no imóvel' },
  {
    key: 'time',
    label: 'Data',
    render: (row) => (
      <span>
        {row.date} às {row.time}
      </span>
    ),
  },
];

const companyProfileInfo = {
  tradeName: 'Imobiliária Demo',
  companyName: 'Imobiliária Demo LTDA',
  document: '12.345.678/0001-90',
  addressLine: 'Avenida Ataulfo de Paiva, 50',
  addressComplement: 'Leblon, Rio de Janeiro - RJ',
  zipCode: '22440-033',
};

const operationCities = [
  ['rio', 'Rio de Janeiro', 'Leblon, Ipanema, Botafogo'],
  ['sao-paulo', 'São Paulo', 'Pinheiros'],
];

function ProfileInfoCard({ icon, title, children }) {
  return (
    <section style={styles.profileInfoCard}>
      <span aria-hidden="true" style={styles.profileIcon}>{icon}</span>
      <div>
        <h2 style={{ ...styles.sectionTitle, fontSize: 18, marginBottom: 10 }}>{title}</h2>
        <div style={{ color: colors.text, lineHeight: 1.55 }}>{children}</div>
      </div>
    </section>
  );
}

ProfileInfoCard.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};

export function HomePage() {
  const [activeTab, setActiveTab] = useState('OFFERS');
  const visitRows = visits.slice(0, 3);
  const offerRows = rentOffers.slice(0, 3);

  return (
    <>
      <PageIntro
        title="Boas-vindas à carteira de Imobiliária Demo"
        subtitle="Acompanhe aqui o status dos seus imóveis no QuintoAndar."
      />

      <Alert kind="info" title="Defina as áreas de atuação da imobiliária">
        As áreas de atuação são essenciais para que a imobiliária receba os melhores leads do QuintoAndar.
        Essas áreas serão padrão para os novos corretores cadastrados e poderão ser editadas individualmente a qualquer momento.
        <div style={{ marginTop: 12 }}>
          <ButtonLink href="/operation-areas?contextType=COMPANY&isEditMode=true">
            Definir área de atuação
          </ButtonLink>
        </div>
      </Alert>

      <Section title="Principais atividades">
        <div style={styles.taskGrid}>
          {homeTaskCards.map((card) => (
            <TaskCard card={card} key={card.id} />
          ))}
        </div>
      </Section>

      <Section
        actions={
          <Tabs
            active={activeTab}
            items={[
              ['OFFERS', 'Propostas de aluguel'],
              ['VISITS', 'Visitas agendadas'],
            ]}
            onChange={setActiveTab}
          />
        }
        title={activeTab === 'OFFERS' ? 'Propostas' : 'Visitas'}
      >
        {activeTab === 'OFFERS' ? (
          <>
            <DataTable columns={columnsForNextOffers} rows={offerRows} />
            <div style={{ marginTop: 12 }}>
              <ButtonLink href="/rent-offers/IN_NEGOTIATION" variant="quiet">
                Ver todas as propostas recebidas
              </ButtonLink>
            </div>
          </>
        ) : (
          <>
            <DataTable columns={columnsForVisits} rows={visitRows} />
            <div style={{ marginTop: 12 }}>
              <ButtonLink href="/visits" variant="quiet">
                Ver todas as visitas
              </ButtonLink>
            </div>
          </>
        )}
      </Section>
    </>
  );
}

export function StatusPage({ pathname, params }) {
  const [active, setActive] = useState(statusFromPath(pathname, params.status));
  const [message, setMessage] = useState(null);
  const rows = active === 'all' ? statusRows : statusRows.filter((row) => row.statusGroup === active);

  return (
    <>
      <PageIntro
        actions={<ButtonLink href="/house-registration">Cadastrar imóvel</ButtonLink>}
        title="Meus imóveis"
        subtitle="Após o envio, o imóvel pode levar entre 24 e 48h para ser refletido em seus imóveis no Marketplace QuintoAndar."
      />
      <Card style={{ display: 'grid', gap: 16 }}>
        <SearchBox
          onSearch={(value) => {
            setMessage(`Busca enviada: ${value}`);
          }}
          placeholder="Buscar por endereço ou código"
        />
        {message ? <Alert kind="success">{message}</Alert> : null}
        <Tabs active={active} items={statusTabs} onChange={setActive} />
        <div>
          <strong>Motivo</strong>
          <div style={{ ...styles.row, marginTop: 10 }}>
            {statusReasonGroups.map(([group, reason]) => (
              <Tag key={`${group}-${reason}`} kind={group === 'Desativado' || group === 'Não elegível' ? 'critical' : 'warning'}>
                {reason}
              </Tag>
            ))}
          </div>
        </div>
      </Card>
      <Section title="Meus imóveis">
        {active === 'error' ? (
          <FeedbackBlock
            kind="critical"
            title="Ocorreu um erro"
            subtitle="Houve algum problema ao recuperar seus dados, favor tente mais tarde"
          />
        ) : rows.length ? (
          <DataTable
            columns={[
              { key: 'crmId', label: 'ID Imobiliária' },
              {
                key: 'address',
                label: 'Imóvel',
                render: (row) => <PropertyCell property={propertyById(row.propertyId)} />,
              },
              { key: 'status', label: 'Status', render: (row) => <Tag>{row.status}</Tag> },
              { key: 'businessContext', label: 'Anúncio' },
              { key: 'value', label: 'Valor', render: (row) => money(row.value) },
              {
                key: 'detail',
                label: 'Detalhamento',
                render: (row) => (
                  <div>
                    <strong>{row.detail}</strong>
                    <p style={{ ...styles.caption, marginTop: 4 }}>{row.reason}</p>
                    <ButtonLink href={`/property/${row.propertyId}/summary`} variant="quiet">
                      {row.action}
                    </ButtonLink>
                  </div>
                ),
              },
            ]}
            rows={rows}
          />
        ) : (
          <FeedbackBlock
            title="Não foram encontrados anúncios para os filtros selecionados"
            subtitle="Experimente remover ou selecionar outros filtros."
          />
        )}
      </Section>
    </>
  );
}

StatusPage.propTypes = {
  params: PropTypes.object,
  pathname: PropTypes.string.isRequired,
};

export function RegistrationPage({ section }) {
  const [activeStep, setActiveStep] = useState(section === 'location' ? 'location' : 'propertyDetails');
  const [assistantModal, setAssistantModal] = useState(false);
  const activeRegistrationStep = registrationSteps.find((step) => step.id === activeStep) || registrationSteps[1];

  if (section === 'success') {
    return (
      <>
        <PageIntro
          title="Pronto, esse imóvel foi pré-cadastrado!"
          subtitle="Acompanhe os próximos passos para publicação do imóvel"
        />
        <FeedbackBlock
          kind="positive"
          title="Pronto, agora é só enviar o link de confirmação para o proprietário!"
          subtitle="Também vamos enviar uma mensagem automática para o proprietário."
          action={
            <div style={styles.row}>
              <ButtonLink>Enviar link via Whatsapp</ButtonLink>
              <ButtonLink href="/status" variant="secondary">Finalizar</ButtonLink>
            </div>
          }
        />
        <Section title="Copie o link e envie para o proprietário">
          <Card>
            <p style={{ ...styles.caption, marginBottom: 14, whiteSpace: 'pre-line' }}>
              {`Olá, Helena Paiva!
Pré-cadastrei seu imóvel para aluguel e venda, localizado em:

Avenida Ataulfo de Paiva, 50 - Leblon, Rio de Janeiro

*Para confirmar o cadastro do imóvel, clique no link a seguir*`}
            </p>
            <Timeline
              items={registrationSuccessSteps.map(([title, description]) => ({ title, description }))}
            />
            <Alert kind="success" title="Prontinho! Estamos preparando seu anúncio">
              Agora, aguarde a sessão de fotos e a publicação do anúncio. Data e horário das fotos: quarta-feira, 15/07/2026, às 10:30.
            </Alert>
            <Alert kind="info" title="Erro ao carregar link de confirmação">
              Pronto, agora é só enviar o link para o proprietário confirmar a publicação e a representação!
            </Alert>
          </Card>
        </Section>
      </>
    );
  }

  return (
    <>
      <div style={styles.registrationAppBar}>
        <ButtonLink href="/portfolio-manager" variant="quiet">Voltar</ButtonLink>
        <h1 style={styles.registrationAppBarTitle}>Cadastro do Imóvel</h1>
        <ButtonLink href="/drafts/ongoing" variant="quiet">Salvar e sair</ButtonLink>
      </div>

      <div style={styles.registrationLayout}>
        <aside aria-label="Etapas do cadastro" style={styles.registrationStepsPanel}>
          {registrationSteps.map((step, index) => {
            const isActive = step.id === activeStep;
            return (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                style={{
                  ...styles.registrationStepButton,
                  background: isActive ? colors.primarySoft : 'transparent',
                }}
                type="button"
              >
                <span style={styles.registrationStepIndex}>{index + 1}</span>
                <span>
                  <strong style={{ display: 'block', lineHeight: 1.35 }}>{step.title}</strong>
                  <span style={{ ...styles.caption, display: 'block', marginTop: 4 }}>{step.description}</span>
                </span>
              </button>
            );
          })}
        </aside>

        <section style={styles.registrationFormShell}>
          <h2 style={styles.registrationFormTitle}>{activeRegistrationStep.title}</h2>
          <div style={styles.registrationFormCard}>
            <div style={styles.registrationFormBody}>
              {activeStep === 'location' ? <LocationForm /> : null}
              {activeStep === 'propertyDetails' ? <PropertyDetailsForm /> : null}
              {activeStep === 'ownerInformation' ? (
                <OwnerForm onAssistant={() => setAssistantModal(true)} />
              ) : null}
              {activeStep === 'rentPricing' || activeStep === 'salePricing' ? <PricingForm /> : null}
              {activeStep === 'photoScheduling' ? <UploadList files={registrationFiles} /> : null}
              {activeStep === 'rentVisitPriority' || activeStep === 'saleVisitPriority' ? <VisitPriorityForm /> : null}
            </div>
            <div style={styles.registrationBottomBar}>
              <ButtonLink variant="secondary">Voltar</ButtonLink>
              <div style={styles.row}>
                {activeStep === 'ownerInformation' ? (
                  <ButtonLink onClick={() => setAssistantModal(true)} variant="secondary">
                    Continuar com assistente
                  </ButtonLink>
                ) : null}
                {activeStep === 'photoScheduling' ? (
                  <ButtonLink variant="secondary">Agendar depois</ButtonLink>
                ) : null}
                <ButtonLink href={activeStep === 'saleVisitPriority' ? '/house-registration/success' : undefined}>
                  {activeStep === 'saleVisitPriority' ? 'Finalizar' : 'Continuar'}
                </ButtonLink>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Modal
        actions={
          <>
            <ButtonLink onClick={() => setAssistantModal(false)} variant="secondary">
              Sair do cadastro
            </ButtonLink>
            <ButtonLink onClick={() => setAssistantModal(false)}>
              Continuar sem assistente
            </ButtonLink>
          </>
        }
        onClose={() => setAssistantModal(false)}
        open={assistantModal}
        title="Não podemos contactar esse proprietário"
      >
        Esse proprietário indicou que não quer receber comunicações do QuintoAndar. Se quiser, siga com o cadastro sem o uso da assistente e entre em contato diretamente com o proprietário.
        <Alert kind="info" title="Já falamos com este proprietário">
          Nos últimos 30 dias, já consultamos o proprietário sobre anunciar este imóvel. Se quiser, siga com o cadastro sem o uso da assistente.
        </Alert>
      </Modal>
    </>
  );
}

RegistrationPage.propTypes = {
  section: PropTypes.string,
};

export function DraftsPage({ section = 'ongoing' }) {
  const isSubmitted = section === 'submitted';
  const [active, setActive] = useState(isSubmitted ? 'submitted' : 'ongoing');
  const [drawer, setDrawer] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const rows = draftRows.filter((draft) => draft.draftSection === active);

  return (
    <>
      <PageIntro
        actions={<ButtonLink href="/house-registration">Cadastrar imóvel</ButtonLink>}
        title={active === 'ongoing' ? 'Rascunhos para finalizar' : 'Rascunhos para confirmar'}
        subtitle={active === 'ongoing' ? 'Continue o cadastro dos imóveis para o proprietário confirmar.' : 'Envie o link para o proprietário confirmar suas captações de imóveis.'}
      />
      <Card style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
        <Tabs
          active={active}
          items={[
            ['ongoing', 'Rascunhos para finalizar'],
            ['submitted', 'Rascunhos para confirmar'],
          ]}
          onChange={setActive}
        />
        <Alert kind="critical" title="Tivemos um problema ao exibir as informações">
          Por favor, aguarde alguns minutos e tente novamente.
          <div style={{ marginTop: 10 }}>
            <ButtonLink variant="secondary">Tentar Novamente</ButtonLink>
          </div>
        </Alert>
      </Card>
      {rows.length ? (
        <DataTable
          columns={[
            { key: 'property', label: 'Imóvel', render: (row) => <PropertyCell property={propertyById(row.propertyId)} /> },
            { key: 'owner', label: 'Proprietário' },
            { key: 'status', label: 'Status', render: (row) => <Tag>{row.status}</Tag> },
            { key: 'updatedAt', label: active === 'ongoing' ? 'Última edição' : 'Atualizado em' },
            { key: 'reason', label: 'Motivo' },
            {
              key: 'actions',
              label: '',
              render: (row) => (
                <div style={styles.row}>
                  {row.draftSection === 'ongoing' ? (
                    <ButtonLink href="/house-registration" variant="secondary">Continuar cadastro</ButtonLink>
                  ) : (
                    <ButtonLink onClick={() => setDrawer(row)} variant="secondary">Reenviar confirmação</ButtonLink>
                  )}
                  <ButtonLink onClick={() => setDeleteModal(true)} variant="danger">Deletar rascunho</ButtonLink>
                </div>
              ),
            },
          ]}
          rows={rows}
        />
      ) : (
        <FeedbackBlock
          title={active === 'ongoing' ? 'Nenhum rascunho salvo' : 'Nenhum pré-cadastro enviado'}
          subtitle={active === 'ongoing' ? 'Comece a cadastrar um imóvel para ver seus rascunhos aqui.' : 'Caso queira ver os cadastros confimados, acesse o menu Imóveis.'}
        />
      )}
      <Drawer
        onClose={() => setDrawer(null)}
        open={Boolean(drawer)}
        subtitle="Envie o link de confirmação para o proprietário"
        title="Reenviar confirmação"
      >
        <Alert kind="info" title="O link abaixo pode ser copiado e enviado">
          Você pode reenviar o link de confirmação para qualquer número de telefone do proprietário, mesmo que seja diferente do cadastrado.
        </Alert>
        <DetailList
          rows={[
            ['Dados do proprietário', drawer?.owner || 'Não preenchido'],
            ['Celular', '+55 21 98888-1200'],
            ['Contexto', drawer?.businessContext || 'aluguel e venda'],
            ['Link', 'https://quintoandar.com.br/confirmacao/draft-1002'],
          ]}
        />
        <p style={{ ...styles.caption, whiteSpace: 'pre-line' }}>
          {`Olá, ${drawer?.owner || 'Helena Paiva'}!
Pré-cadastrei seu imóvel para ${drawer?.businessContext || 'aluguel e venda'}, localizado em:

${drawer ? propertyById(drawer.propertyId).address : properties[0].address}

*Para confirmar o cadastro do imóvel, clique no link a seguir*`}
        </p>
        <div style={styles.row}>
          <ButtonLink>Enviar via Whatsapp</ButtonLink>
          <ButtonLink variant="secondary">Link copiado com sucesso!</ButtonLink>
        </div>
        <Alert kind="critical" title="Pré-cadastro não encontrado">
          As informações do pré-cadastro selecionado não foram encontradas. Por favor, atualize a página para exibir os pré-cadastros disponíveis.
        </Alert>
      </Drawer>
      <Modal
        actions={
          <>
            <ButtonLink onClick={() => setDeleteModal(false)} variant="secondary">Voltar</ButtonLink>
            <ButtonLink onClick={() => setDeleteModal(false)} variant="danger">Deletar rascunho</ButtonLink>
          </>
        }
        onClose={() => setDeleteModal(false)}
        open={deleteModal}
        title="Deletar rascunho"
      >
        Ao deletar este rascunho todas as informações salvas serão excluídas e não será possível confirmar o cadastro.
      </Modal>
    </>
  );
}

DraftsPage.propTypes = {
  section: PropTypes.string,
};

export function PhotosPage() {
  const [active, setActive] = useState('ALL');
  const rows = active === 'ALL' ? photoSessions : photoSessions.filter((session) => session.filter === active);

  return (
    <>
      <PageIntro title="Sessões de Fotos" subtitle="Agende a sessão de fotos para o imóvel ser publicado." />
      <Card style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
        <Tabs
          active={active}
          items={[
            ['TO_SCHEDULE', 'Para agendar'],
            ['SCHEDULED', 'Agendadas'],
            ['WAITING_PUBLICATION', 'Aguardando publicação'],
            ['ALL', 'Todas'],
          ]}
          onChange={setActive}
        />
        <Alert kind="critical" title="Tivemos um problema ao exibir as informações">
          Por favor, aguarde alguns minutos e tente novamente.
        </Alert>
      </Card>
      {rows.length ? (
        <DataTable
          columns={[
            { key: 'session', label: 'Sessão' },
            { key: 'property', label: 'Imóvel', render: (row) => <PropertyCell property={propertyById(row.propertyId)} /> },
            { key: 'entry', label: 'Entrada' },
            { key: 'status', label: 'Status', render: (row) => <Tag>{row.status}</Tag> },
            { key: 'availableFrom', label: 'A partir de' },
            {
              key: 'actions',
              label: '',
              render: (row) => (
                <ButtonLink variant="secondary">
                  {row.filter === 'SCHEDULED' ? 'Reagendar fotos' : 'Agendar fotos'}
                </ButtonLink>
              ),
            },
          ]}
          rows={rows}
        />
      ) : (
        <FeedbackBlock
          title="Nenhuma sessão de fotos"
          subtitle="Quando tiverem imóveis com sessão de fotos, será exibido aqui."
        />
      )}
    </>
  );
}

function LocationForm() {
  return (
    <div style={styles.formGrid}>
      <Field label="Endereço" value="Avenida Ataulfo de Paiva" />
      <Field label="Número" value="50" />
      <Field label="Complemento" value="1201" />
      <Field label="Bairro" value="Leblon" />
      <Field label="Cidade" value="Rio de Janeiro" />
      <Field label="CEP" value="22440-033" />
      <Field helper="regionId usado para validar atuação e disponibilidade" label="regionId" value="RIO_ZONA_SUL_LEBLON" />
      <div>
        <span style={styles.label}>Sem número</span>
        <ButtonLink variant="secondary">Continuar sem número</ButtonLink>
      </div>
    </div>
  );
}

function PropertyDetailsForm() {
  return (
    <div style={styles.formGrid}>
      <Field as="select" label="Qual o tipo de imóvel?" options={['Apartamento', 'Kitnet/Studio', 'Casa', 'Casa em condomínio']} value="Apartamento" />
      <Field helper="Se não souber a área exata, inclua um valor aproximado" label="Área do imóvel" value="112" />
      <Field helper="Incluindo suítes" label="Qual o número de quartos?" value="3" />
      <Field helper="Não incluir lavabo e serviço" label="Qual o número de banheiros?" value="2" />
      <Field label="Destes, quantos são suites?" value="1" />
      <Field label="Qual o número de vagas de garagem?" value="1" />
      <Field as="select" label="Inquilinos podem ter animais de estimação?" options={['Sim', 'Não', 'Não sei']} value="Sim" />
      <Field as="textarea" label="Alguns desses itens vai ficar no imóvel?" value="Armário na cozinha, fogão/cooktop, geladeira e armário no quarto." />
      <div>
        <span style={styles.label}>Número do endereço</span>
        <ButtonLink variant="secondary">Continuar sem número</ButtonLink>
      </div>
    </div>
  );
}

function OwnerForm({ onAssistant }) {
  return (
    <div style={styles.formGrid}>
      <Field label="Nome do proprietário" value="Helena Paiva" />
      <Field label="Telefone" value="+55 21 98888-1200" />
      <Field label="Email" value="helena.paiva@email.com" />
      <Field as="select" label="Pessoa proprietária aceita contato da assistente?" options={['Sim', 'Não', 'Não sei']} value="Sim" />
      <div>
        <span style={styles.label}>Assistente</span>
        <ButtonLink onClick={onAssistant}>Continuar com assistente</ButtonLink>
      </div>
    </div>
  );
}

OwnerForm.propTypes = {
  onAssistant: PropTypes.func.isRequired,
};

function PricingForm() {
  return (
    <div style={styles.formGrid}>
      <CurrencyField label="Aluguel" value="7.200" />
      <CurrencyField label="IPTU" value="600" />
      <CurrencyField label="Condomínio" value="1.450" />
      <CurrencyField label="Proprietário vai receber" value="6.480" />
      <CurrencyField label="Valor de venda" value="1.520.000" />
      <Alert kind="info" title="Análise de preço">
        Imóveis como este costumam atrair menos interessados quando têm preço acima de R$ 7.000.
      </Alert>
    </div>
  );
}

function VisitPriorityForm() {
  return (
    <div style={styles.formGrid}>
      <Field as="select" label="Entrada no imóvel" options={['Chaves com corretor', 'Chaves na portaria', 'Proprietário abre o imóvel']} value="Chaves com corretor" />
      <Field as="select" label="Visitas de aluguel" options={['Manhã', 'Tarde', 'Noite']} value="Manhã" />
      <Field as="select" label="Visitas de venda" options={['Manhã', 'Tarde', 'Noite']} value="Tarde" />
      <Field as="textarea" label="Instruções de entrada" value="Retirar chaves com o corretor responsável antes da visita." />
    </div>
  );
}

export function PropertyPage({ params, pathname }) {
  const section = propertySectionFromPath(pathname);
  const property = propertyById(params.propertyId || pathname.split('/')[2]);
  const [drawerMode, setDrawerMode] = useState(null);

  return (
    <>
      <PageIntro
        actions={<ButtonLink onClick={() => setDrawerMode('MENU')}>Editar imóvel</ButtonLink>}
        title="Detalhe do imóvel"
        subtitle={<PropertyCell property={property} suppressLink />}
      />
      <Tabs
        active={section}
        items={propertyTabs}
        onChange={(value) => {
          window.location.href = `/property/${property.id}/${value}`;
        }}
      />
      {section === 'summary' ? <PropertySummary property={property} onEdit={() => setDrawerMode('MENU')} /> : null}
      {section === 'details' ? <PropertyDetails property={property} onEdit={() => setDrawerMode('MENU')} /> : null}
      {section === 'visits' ? <VisitsEmbedded propertyId={property.id} /> : null}
      {section === 'rent_offers' ? <RentOffersEmbedded propertyId={property.id} /> : null}
      <EditHubDrawer
        mode={drawerMode}
        onChangeMode={setDrawerMode}
        onClose={() => setDrawerMode(null)}
        property={property}
      />
    </>
  );
}

PropertyPage.propTypes = {
  params: PropTypes.object,
  pathname: PropTypes.string.isRequired,
};

function PropertySummary({ onEdit, property }) {
  return (
    <div style={{ ...styles.twoColumn, marginTop: 20 }}>
      <Card>
        <h2 style={styles.sectionTitle}>Status do anúncio</h2>
        <div style={{ ...styles.row, marginTop: 12 }}>
          <Tag>{property.status}</Tag>
          {property.businessContexts.map((context) => (
            <Tag key={context} kind="neutral">{context}</Tag>
          ))}
        </div>
        <p style={{ ...styles.caption, marginTop: 12 }}>{property.description}</p>
        <div style={{ marginTop: 16 }}>
          <ButtonLink onClick={onEdit} variant="secondary">Editar imóvel</ButtonLink>
        </div>
      </Card>
      <PriceTable pricing={property} />
      <Card>
        <h2 style={styles.sectionTitle}>Valores do proprietário</h2>
        <DetailList
          rows={[
            ['Alugue por', money(property.rentPrice)],
            ['Venda por', money(property.salePrice)],
            ['Total a receber', money(property.ownerEarns)],
            ['Taxa de administração', money(720)],
            ['Taxa de corretagem', money(1200)],
          ]}
        />
        <Alert kind="warning" title="Valor fora da faixa praticada">
          Insira um valor entre R$ 2.500 e R$ 18.000.
        </Alert>
      </Card>
      <Card>
        <h2 style={styles.sectionTitle}>O anúncio está sendo preparado</h2>
        <Timeline
          items={[
            { title: 'Cadastro confirmado pelo proprietário', description: 'Lead aceito na Central da Imobiliária.' },
            { title: 'Só falta agendar a sessão de fotos!', description: 'Durante a sessão, o fotógrafo coleta dados para completar o anúncio.' },
            { title: 'Publicação', description: 'Após o envio, o imóvel pode levar entre 24 e 48h para aparecer no Marketplace.' },
          ]}
        />
      </Card>
    </div>
  );
}

PropertySummary.propTypes = {
  onEdit: PropTypes.func.isRequired,
  property: PropTypes.object.isRequired,
};

function PropertyDetails({ onEdit, property }) {
  return (
    <div style={{ display: 'grid', gap: 20, marginTop: 20 }}>
      <Card>
        <h2 style={styles.sectionTitle}>Descrição do imóvel</h2>
        <p style={{ ...styles.caption, marginTop: 8 }}>{property.description}</p>
        <div style={{ marginTop: 12 }}>
          <ButtonLink variant="secondary">Confira exemplo de descrição</ButtonLink>
        </div>
      </Card>
      <Card>
        <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={styles.sectionTitle}>Características</h2>
          <ButtonLink onClick={onEdit} variant="secondary">Editar características do imóvel</ButtonLink>
        </div>
        <DetailList
          rows={[
            ['Tipo', property.propertyType],
            ['Área do imóvel', `${property.area} m²`],
            ['Quartos', property.bedrooms],
            ['Banheiros', property.bathrooms],
            ['Suítes', property.suites],
            ['Vagas de garagem', property.parking],
          ]}
        />
      </Card>
      <Section title="Itens disponíveis no imóvel">
        <Card>
          <div style={styles.row}>
            {property.availableItems.map((item) => (
              <Tag key={item} kind="neutral">{item}</Tag>
            ))}
          </div>
        </Card>
      </Section>
      <Section title="Vendidos e alugados no QuintoAndar">
        <div style={styles.grid}>
          {similarTransactions.map((transaction) => (
            <Card key={transaction.id}>
              <Tag kind="neutral">{transaction.label}</Tag>
              <h3 style={{ fontSize: 16, margin: '12px 0 4px' }}>{transaction.address}</h3>
              <p style={styles.caption}>{transaction.context} · {transaction.date}</p>
              <strong style={{ display: 'block', marginTop: 12 }}>{money(transaction.price)}</strong>
              <span style={styles.caption}>Valor por m²: {money(transaction.price / transaction.area)}</span>
            </Card>
          ))}
        </div>
      </Section>
      <Section title="Preço ideal">
        <div style={styles.grid}>
          {propertyPriceBands.map(([title, description]) => (
            <Card key={title}>
              <h3 style={{ fontSize: 16, margin: 0 }}>{title}</h3>
              <p style={{ ...styles.caption, marginTop: 8 }}>{description}</p>
            </Card>
          ))}
        </div>
      </Section>
      <Section title="Valores do comprador">
        <Card>
          <DetailList
            rows={[
              ['Total a pagar', money(property.salePrice)],
              ['Valores do comprador', 'Financiamento, FGTS e sinal'],
              ['Valores do inquilino', 'Seguro incêndio e taxa de serviço'],
              ['Seguro incêndio', money(42)],
              ['Taxa de serviço', money(290)],
            ]}
          />
        </Card>
      </Section>
    </div>
  );
}

PropertyDetails.propTypes = {
  onEdit: PropTypes.func.isRequired,
  property: PropTypes.object.isRequired,
};

function VisitsEmbedded({ propertyId }) {
  return (
    <Section title="Visitas" subtitle="Visitas não confirmadas e próximas visitas do imóvel.">
      <DataTable columns={columnsForVisits} rows={visits.filter((visit) => visit.propertyId === propertyId)} />
    </Section>
  );
}

VisitsEmbedded.propTypes = {
  propertyId: PropTypes.string.isRequired,
};

function RentOffersEmbedded({ propertyId }) {
  return (
    <Section title="Propostas de aluguel" subtitle="Propostas próximas de expirar.">
      <DataTable columns={columnsForNextOffers} rows={rentOffers.filter((offer) => offer.propertyId === propertyId)} />
    </Section>
  );
}

RentOffersEmbedded.propTypes = {
  propertyId: PropTypes.string.isRequired,
};

function EditHubDrawer({ mode, onChangeMode, onClose, property }) {
  const isOpen = Boolean(mode);
  const title = mode === 'PRICE' ? 'Editar preço' : mode === 'STATUS' ? 'Status do anúncio' : mode === 'ENTRY_DETAILS' ? 'Editar entrada no imóvel' : 'Editar anúncio de Aluguel';
  const subtitle = mode === 'MENU' ? 'Escolha o que deseja editar no anúncio.' : null;

  return (
    <Drawer onClose={onClose} open={isOpen} subtitle={subtitle} title={title}>
      {mode === 'MENU' ? (
        propertyEditOptions.map(([id, label, description]) => (
          <button
            key={id}
            onClick={() => onChangeMode(id === 'PRICE' ? 'PRICE' : id === 'ENTRY_DETAILS' ? 'ENTRY_DETAILS' : id === 'STATUS' ? 'STATUS' : 'MENU')}
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              cursor: 'pointer',
              font: 'inherit',
              padding: 14,
              textAlign: 'left',
            }}
            type="button"
          >
            <strong>{label}</strong>
            <p style={{ ...styles.caption, marginTop: 4 }}>{description}</p>
          </button>
        ))
      ) : null}
      {mode === 'PRICE' ? (
        <>
          <Alert kind="warning" title="Alterações feitas aqui são somente para o QuintoAndar">
            Caso queira alterar em todos os seus canais faça esse ajuste diretamente no seu CRM.
          </Alert>
          <CurrencyField label="Aluguel" value="7.200" />
          <CurrencyField label="Valor de venda" value="1.520.000" />
          <ButtonLink>Salvar alterações</ButtonLink>
        </>
      ) : null}
      {mode === 'ENTRY_DETAILS' ? (
        <>
          <Field as="select" label="Entrada no imóvel" options={['Chaves com corretor', 'Chaves na portaria', 'Proprietário abre o imóvel']} value={property.entry} />
          <Field as="textarea" label="Instruções de entrada" value="Retirar chaves com o corretor responsável." />
          <ButtonLink>Salvar alterações</ButtonLink>
        </>
      ) : null}
      {mode === 'STATUS' ? (
        <>
          <Alert title="Deseja reativar o anúncio para aluguel?">
            Você poderá editar as características do anúncio após a republicação.
          </Alert>
          <ButtonLink>Reativar anúncio</ButtonLink>
          <ButtonLink variant="secondary">Pausar anúncio</ButtonLink>
        </>
      ) : null}
    </Drawer>
  );
}

EditHubDrawer.propTypes = {
  mode: PropTypes.string,
  onChangeMode: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  property: PropTypes.object.isRequired,
};

export function SearchPage({ params, pathname }) {
  const term = extractSearchTerm(pathname, params);
  const type = /^A|^B|^C/i.test(term) ? 'CRM_ID' : /^\d+$/.test(term) ? 'PROPERTY_ID' : 'ADDRESS';
  const placeholderType = type === 'CRM_ID' ? 'SEARCH_BY_CRM_ID' : type === 'PROPERTY_ID' ? 'SEARCH_BY_PROPERTY_ID' : 'SEARCH_BY_ADDRESS_OR_PROPERTY_ID';
  const rows = properties.filter((property) =>
    [property.id, property.crmId, property.address].some((value) => String(value).toLowerCase().includes(term.toLowerCase())),
  );

  return (
    <>
      <PageIntro title="Resultados da busca" subtitle={`Tipo de busca: ${type}`} />
      <Card style={{ marginBottom: 20 }}>
        <strong>{placeholderType}</strong>
        <p style={{ ...styles.caption, marginTop: 4 }}>Buscar por id (código de seu CRM), endereço ou código.</p>
      </Card>
      <DataTable
        columns={[
          {
            key: 'property',
            label: 'Imóvel',
            render: (property) => <PropertyCell property={property} />,
          },
          { key: 'status', label: 'Status', render: (property) => <Tag>{property.status}</Tag> },
          { key: 'businessContexts', label: 'Anúncio', render: (property) => property.businessContexts.join(' / ') },
        ]}
        rows={rows.length ? rows : properties}
        rowKey="id"
      />
    </>
  );
}

SearchPage.propTypes = {
  params: PropTypes.object,
  pathname: PropTypes.string.isRequired,
};

export function VisitsPage() {
  const [active, setActive] = useState('ALL');
  const [modal, setModal] = useState(false);
  const rows = active === 'ALL' ? visits : visits.filter((visit) => visit.filter === active);

  return (
    <>
      <PageIntro
        actions={<ButtonLink onClick={() => setModal(true)}>Revisar horários</ButtonLink>}
        title="Visitas"
        subtitle="Quando você tiver visitas, elas aparecerão aqui para que você possa acompanhar."
      />
      <Card style={{ marginBottom: 20 }}>
        <Tabs
          active={active}
          items={[
            ['ALL', 'Todas'],
            ['MY_SUPPLY', 'Meus imóveis'],
            ['MY_CLIENTS', 'Meus clientes'],
            ['LEAD_GEN', 'Clientes QuintoAndar'],
          ]}
          onChange={setActive}
        />
      </Card>
      {rows.length ? (
        <DataTable columns={columnsForVisits} rows={rows} />
      ) : (
        <FeedbackBlock title="Sem Visitas" subtitle="Quando você tiver visitas, elas aparecerão aqui para que você possa acompanhar." />
      )}
      <Modal
        actions={<ButtonLink onClick={() => setModal(false)}>Certo, entendi</ButtonLink>}
        onClose={() => setModal(false)}
        open={modal}
        title="Aproveite e revise os horários para visitas"
      >
        Evite novos cancelamentos. Selecione abaixo os horários nos quais o imóvel pode receber visitas.
        <Alert kind="critical" title="Horário expirou">
          Essa visita não pode ser cancelada porque o horário expirou.
        </Alert>
        <Alert kind="critical" title="Horário expirou">
          Essa visita não pode ser confirmada porque o horário expirou.
        </Alert>
      </Modal>
    </>
  );
}

export function OffersPage({ kind, pathname }) {
  const [status, setStatus] = useState('IN_NEGOTIATION');
  const [saleSubStatus, setSaleSubStatus] = useState('IN_NEGOTIATION');
  const [modal, setModal] = useState(false);
  const [offerModalMode, setOfferModalMode] = useState('summary');
  const isInternal = pathname.includes('/internal/');

  if (kind === 'sale') {
    const saleSubtitle = isInternal
      ? 'Gerencie e negocie as propostas de venda feitas pelos seus corretores nos imóveis do Marketplace.'
      : status === 'FINISHED'
        ? 'Propostas concluídas nos últimos 60 dias.'
        : status === 'CANCELED'
          ? 'Propostas canceladas nos últimos 60 dias.'
          : 'Propostas recebidas nos últimos 180 dias.';

    return (
      <>
        <PageIntro
          title={isInternal ? 'Minhas propostas de venda' : 'Propostas de venda'}
          subtitle={saleSubtitle}
        />
        <Card style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
          <Tabs
            active={status}
            items={[
              ['IN_NEGOTIATION', 'Em andamento', 2],
              ['FINISHED', 'Concluídas', 1],
              ['CANCELED', 'Canceladas', 0],
            ]}
            onChange={setStatus}
          />
          <Tabs
            active={saleSubStatus}
            items={[
              ['IN_NEGOTIATION', 'Todas'],
              ['ONGOING', 'Recebidas'],
              ['ACCEPTED', 'Aceitas'],
              ['POST_CCV', 'Assinadas'],
            ]}
            onChange={setSaleSubStatus}
            size="small"
          />
          <div>
            <strong>Forma de pagamento</strong>
            <div style={{ ...styles.row, marginTop: 10 }}>
              {salePaymentMethods.map((payment) => (
                <Tag key={payment} kind="neutral">{payment}</Tag>
              ))}
            </div>
          </div>
        </Card>
        {isInternal ? null : (
          <Alert kind="info" title="Propostas de venda externas">
            Acompanhe as propostas de venda realizadas por corretores externos para os imóveis da sua imobiliária.
          </Alert>
        )}
        <DataTable
          columns={[
            { key: 'address', label: 'Imóvel', render: (row) => <PropertyCell property={propertyById(row.propertyId)} /> },
            {
              key: 'buyer',
              label: 'Comprador',
              render: (row) => (
                <div>
                  <strong>{row.buyer}</strong>
                  <p style={styles.caption}>{row.creditStatus}</p>
                  <p style={styles.caption}>{row.payment}</p>
                </div>
              ),
            },
            { key: 'value', label: 'Valor', render: (row) => money(row.value) },
            { key: 'finishedAt', label: 'Concluída em', render: (row) => row.updatedAt },
            { key: 'canceledAt', label: 'Cancelada em', render: (row) => row.status === 'Cancelada' ? row.updatedAt : '-' },
            { key: 'status', label: 'Status', render: (row) => <Tag>{row.status}</Tag> },
          ]}
          rows={saleOffers}
        />
      </>
    );
  }

  return (
    <>
        <PageIntro
          eyebrow="Propostas de aluguel"
          title={status === 'IN_NEGOTIATION' ? 'Propostas de aluguel em negociação' : status === 'ACCEPTED' ? 'Propostas de aluguel aceitas' : 'Propostas de aluguel canceladas'}
          subtitle={status === 'CANCELED' ? 'Propostas canceladas nos últimos 30 dias' : 'As propostas listadas são apenas de imóveis anunciados para aluguel.'}
        />
      <Alert kind="info" title="Esta página exibe apenas informações de imóveis disponíveis para aluguel.">
        Atualizações que acontecerem depois do aceite da propostas não estão sendo exibidas nesta ferramenta, para acompanhar o andamento das propostas consulte a Área do proprietário.
      </Alert>
      <Card style={{ marginBottom: 20 }}>
        <Tabs
          active={status}
          items={[
            ['IN_NEGOTIATION', 'Em andamento', 2],
            ['ACCEPTED', 'Concluídas', 1],
            ['CANCELED', 'Canceladas', 1],
          ]}
          onChange={setStatus}
        />
        <div style={{ ...styles.row, marginTop: 12 }}>
          {[
            'Canceladas durante negociação',
            'Canceladas após aceite',
            'Assinar contrato até hoje',
            'Assinar contrato até amanhã',
            'Preparando contrato',
            'Aguardando interessado',
            'Analisando documentação',
          ].map((label) => (
            <Tag key={label} kind="neutral">{label}</Tag>
          ))}
        </div>
      </Card>
      <DataTable
        columns={[
          { key: 'address', label: 'Imóvel', render: (row) => <PropertyCell property={propertyById(row.propertyId)} /> },
          { key: 'offersCount', label: 'Propostas', render: (row) => row.offersCount },
          { key: 'status', label: 'Status', render: (row) => <Tag>{row.status}</Tag> },
          { key: 'value', label: 'Valor', render: (row) => money(row.value) },
          {
            key: 'actions',
            label: '',
            render: () => (
              <ButtonLink
                onClick={() => {
                  setOfferModalMode('summary');
                  setModal(true);
                }}
                variant="secondary"
              >
                Abrir
              </ButtonLink>
            ),
          },
        ]}
        rows={rentOffers}
      />
      <Modal
        actions={
          offerModalMode === 'counter' ? (
            <>
              <ButtonLink onClick={() => setOfferModalMode('summary')} variant="secondary">Voltar</ButtonLink>
              <ButtonLink onClick={() => setModal(false)}>Enviar contraproposta</ButtonLink>
            </>
          ) : offerModalMode === 'reject' ? (
            <>
              <ButtonLink onClick={() => setOfferModalMode('summary')} variant="secondary">Voltar</ButtonLink>
              <ButtonLink onClick={() => setModal(false)}>Confirmar recusa</ButtonLink>
            </>
          ) : (
            <>
              <ButtonLink onClick={() => setModal(false)} variant="secondary">Fechar</ButtonLink>
              <ButtonLink onClick={() => setModal(false)}>Aceitar proposta</ButtonLink>
              <ButtonLink onClick={() => setOfferModalMode('counter')} variant="secondary">Contraproposta</ButtonLink>
              <ButtonLink onClick={() => setOfferModalMode('reject')} variant="danger">Recusar proposta</ButtonLink>
            </>
          )
        }
        onClose={() => {
          setModal(false);
          setOfferModalMode('summary');
        }}
        open={modal}
        title={offerModalMode === 'counter' ? 'Contraproposta' : offerModalMode === 'reject' ? 'Recusar proposta' : 'Responder proposta RF-301'}
      >
        {offerModalMode === 'summary' ? (
          <>
            <Alert kind="warning" title="Atualizações depois do aceite não estão sendo exibidas">
              Desativar anúncio e cancelar visitas futuras
            </Alert>
            <h3 style={{ fontSize: 16, margin: '14px 0 6px' }}>Resumo financeiro</h3>
            <DetailList rows={[['Valor da proposta', money(7000)], ['Valor do anúncio', money(7200)], ['Status', 'Aguardando resposta do proprietário']]} />
            <p>Propostas em andamento não são garantia de negócio fechado.</p>
          </>
        ) : null}
        {offerModalMode === 'counter' ? (
          <div style={styles.formGrid}>
            <CurrencyField label="Valor da contraproposta" value="7.200" />
            <Field as="textarea" label="Mensagem para o interessado" value="Podemos seguir pelo valor do anúncio e confirmar a entrada em 20/07." />
          </div>
        ) : null}
        {offerModalMode === 'reject' ? (
          <div style={styles.formGrid}>
            <Field as="select" label="Motivo de recusa" options={['Preço abaixo do esperado', 'Data de entrada incompatível', 'Documentação incompleta']} value="Preço abaixo do esperado" />
            <Alert kind="critical">Preço abaixo do esperado</Alert>
            <Alert kind="critical" title="Proposta recusada">
              Esta proposta foi recusada. Para acompanhar outras informações acesse a lista de propostas Canceladas
            </Alert>
            <Field as="textarea" label="Mensagem" value="A proposta foi recusada pelo valor abaixo do esperado." />
          </div>
        ) : null}
      </Modal>
    </>
  );
}

OffersPage.propTypes = {
  kind: PropTypes.string.isRequired,
  pathname: PropTypes.string.isRequired,
};

export function OfferDetailsPage({ kind, pathname }) {
  const [modal, setModal] = useState(false);
  const isRent = kind === 'rent';
  const isClosing = pathname.includes('/closing/');
  const isNegotiation = pathname.includes('/negotiation/');
  const isCanceled = pathname.includes('/canceled/');
  const isTic = pathname.includes('/tic');

  if (isRent) {
    return (
      <>
        <PageIntro
          title={isClosing ? 'Fechamento de aluguel' : isCanceled ? 'Proposta cancelada' : 'Detalhes da proposta de aluguel'}
          subtitle="Proposta, documentação, aceite, recusa e fechamento."
        />
        <div style={styles.twoColumn}>
          <Card>
            <h2 style={styles.sectionTitle}>Documentação do proprietário</h2>
            <p style={styles.caption}>RG, CPF, comprovante de titularidade e dados bancários.</p>
            <DetailList
              rows={[
                ['Status', isClosing ? 'Assinar contrato agora' : isNegotiation ? 'Preparando contrato' : 'Analisando documentação'],
                ['Prazo', isClosing ? 'Assinar contrato até hoje' : 'Assinar contrato até amanhã'],
                ['Ação', isClosing ? 'Enviar documentos' : 'Reenviar documentos'],
              ]}
            />
          </Card>
          <Card>
            <h2 style={styles.sectionTitle}>Documentação do interessado</h2>
            <p style={styles.caption}>Crédito, renda, documentos pessoais e proposta final.</p>
            <Alert kind={isCanceled ? 'critical' : 'info'} title={isCanceled ? 'Essa proposta mudou de status.' : 'Proposta aceita'}>
              {isCanceled ? 'Veja a lista para acompanhar as atualizações.' : 'Para acompanhar outras informações, acesse a lista de propostas aceitas.'}
            </Alert>
          </Card>
        </div>
        <Section title="Fechamento">
          <Card>
            <DetailList
              rows={[
                ['Valor da proposta', money(7000)],
                ['Valor do anúncio', money(7200)],
                ['Interessado', 'Mariana Lima'],
              ]}
            />
            <div style={{ ...styles.row, marginTop: 16 }}>
              <ButtonLink href="/status">Acessar imóveis alugados</ButtonLink>
              <ButtonLink variant="secondary">Voltar para a lista</ButtonLink>
              <ButtonLink variant="secondary">Mostrar propostas aceitas</ButtonLink>
            </div>
          </Card>
        </Section>
      </>
    );
  }

  if (isTic) {
    return (
      <>
        <PageIntro title="TIC" subtitle="Dados para emissão do TIC" />
        <div style={styles.twoColumn}>
          <Card>
            <h2 style={styles.sectionTitle}>Dados para emissão do TIC</h2>
            <DetailList
              rows={[
                ['Comprador', 'Fernanda Costa'],
                ['Forma de pagamento', 'Financiado + FGTS'],
                ['Valor', money(1480000)],
                ['Imóvel', properties[0].address],
              ]}
            />
            <div style={{ ...styles.row, marginTop: 16 }}>
              <ButtonLink onClick={() => setModal(true)}>Revisar documento final</ButtonLink>
              <ButtonLink variant="secondary">Voltar para a proposta</ButtonLink>
            </div>
          </Card>
          <Card>
            <h2 style={styles.sectionTitle}>Preview do TIC preenchido</h2>
            <p style={styles.caption}>Revisão do TIC preenchido antes da emissão.</p>
            <Alert kind="success" title="TIC emitido com sucesso">
              Anexe este PDF ao CCV para coletar as assinaturas necessárias.
            </Alert>
            <ButtonLink variant="secondary">Abrir TIC em PDF</ButtonLink>
          </Card>
        </div>
        <Modal
          actions={
            <>
              <ButtonLink onClick={() => setModal(false)} variant="secondary">Voltar e corrigir</ButtonLink>
              <ButtonLink onClick={() => setModal(false)}>Confirmar emissão</ButtonLink>
            </>
          }
          onClose={() => setModal(false)}
          open={modal}
          title="Revisão do TIC preenchido"
        >
          <p>Preview do TIC preenchido com dados do comprador, imóvel e forma de pagamento.</p>
        </Modal>
      </>
    );
  }

  return (
    <>
      <PageIntro
        title={isTic ? 'TIC' : 'Timeline interativa de venda'}
        subtitle="Timeline interativa com ações, documentos, parceiros e status da venda."
      />
      <div style={styles.twoColumn}>
        <Card>
          <h2 style={styles.sectionTitle}>Timeline interativa com ações</h2>
          <Timeline
            items={[
              { title: 'Proposta recebida', description: 'Comprador enviou proposta para análise.' },
              { title: 'Parceiros da venda', description: 'Corretor interno, corretor externo e imobiliária.' },
              { title: 'TIC', description: 'Documentos em preparação para assinatura.' },
            ]}
          />
        </Card>
        <Card>
          <h2 style={styles.sectionTitle}>Parceiros da venda</h2>
          <DetailList rows={[['Comprador', 'Fernanda Costa'], ['Corretor', 'Claudete Galeasso'], ['Valor', money(1480000)]]} />
          <div style={{ marginTop: 16 }}>
            <ButtonLink onClick={() => setModal(true)}>Abrir ação</ButtonLink>
          </div>
        </Card>
      </div>
      <Modal
        actions={
          <>
            <ButtonLink onClick={() => setModal(false)} variant="secondary">Cancelar</ButtonLink>
            <ButtonLink onClick={() => setModal(false)}>Confirmar</ButtonLink>
          </>
        }
        onClose={() => setModal(false)}
        open={modal}
        title="Ação da proposta"
      >
        <h3 style={{ fontSize: 16, margin: '0 0 8px' }}>Atualizar etapa da proposta</h3>
        <p>Confirme a ação da timeline interativa antes de seguir para os próximos documentos.</p>
        <DetailList rows={[['Composição da venda', 'Financiado + FGTS'], ['Próxima etapa', 'Documentos do imóvel'], ['Ação', 'Confirmar atualização']]} />
      </Modal>
    </>
  );
}

OfferDetailsPage.propTypes = {
  kind: PropTypes.string.isRequired,
  pathname: PropTypes.string.isRequired,
};

const requiredPricingFields = [
  'Informe o tipo do imóvel',
  'Informe o endereço',
  'Informe o número do imóvel',
  'Informe a cidade',
  'Informe a área total',
  'Informe a quantidade de banheiros',
  'Informe a quantidade de quartos',
  'Informe a quantidade de vagas de garagem',
  'Informe o IPTU anual',
];

export function PricePerformancePage({ mode, result }) {
  const [activePricingContext, setActivePricingContext] = useState('sale');
  const isPerformance = mode === 'performance';

  if (result === 'insufficient') {
    const missingFields = activePricingContext === 'rent'
      ? [...requiredPricingFields, 'Informe o valor do condomínio por mês']
      : requiredPricingFields;

    return (
      <>
        <div style={{ maxWidth: 760 }}>
          <Tabs
            active={activePricingContext}
            items={[
              ['sale', 'Para vender'],
              ['rent', 'Para alugar'],
            ]}
            onChange={setActivePricingContext}
          />
          <Card style={{ display: 'grid', gap: 16, marginTop: 16 }}>
            <div>
              <h1 style={{ ...styles.title, fontSize: 28 }}>Dados insuficientes</h1>
              <p style={{ ...styles.subtitle, maxWidth: 640 }}>
                Para que possamos identificar as características do imóvel e sugerir um preço competitivo, é necessário que você forneça os seguintes dados:
              </p>
            </div>
            <div style={{ borderTop: `1px solid ${colors.border}` }}>
              {missingFields.map((item) => (
                <div
                  key={item}
                  style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'grid',
                    gap: 12,
                    gridTemplateColumns: '28px 1fr',
                    padding: '14px 0',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      alignItems: 'center',
                      background: colors.surfaceAlt,
                      borderRadius: 999,
                      color: colors.primary,
                      display: 'inline-flex',
                      fontSize: 14,
                      fontWeight: 900,
                      height: 28,
                      justifyContent: 'center',
                      width: 28,
                    }}
                  >
                    !
                  </span>
                  <strong>{item}</strong>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <PageIntro
        title={isPerformance ? 'Performance do anúncio' : result ? 'Laudo de preço' : 'Calculadora de Imóveis'}
        subtitle={isPerformance ? 'Indicadores, atividade e relatório pós-publicação.' : 'Descubra o valor correto para o imóvel.'}
      />
      <div style={styles.grid}>
        {(isPerformance ? priceActivities : reportMetrics).map(([label, value, variation]) => (
          <Card key={label}>
            <span style={styles.caption}>{label}</span>
            <strong style={{ display: 'block', fontSize: 28, marginTop: 6 }}>{value}</strong>
            {variation ? <Tag kind="success">{variation}</Tag> : null}
          </Card>
        ))}
      </div>
      <Section title={isPerformance ? 'Atividade do anúncio' : 'Imóveis similares'}>
        <DataTable
          columns={[
            { key: 'address', label: 'Imóvel' },
            { key: 'context', label: 'Contexto' },
            { key: 'price', label: 'Valor', render: (row) => money(row.price) },
            { key: 'label', label: 'Comparativo' },
          ]}
          rows={similarTransactions}
        />
      </Section>
    </>
  );
}

PricePerformancePage.propTypes = {
  mode: PropTypes.string,
  result: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export function ListingReportSetupPage() {
  return (
    <>
      <PageIntro
        title="Performance do anúncio"
        subtitle="Acha que esse imóvel precisa de um ajuste no preço sugerido? Edite o valor sugerido."
      />
      <div style={styles.twoColumn}>
        <Card>
          <PropertyCell property={properties[0]} />
          <hr style={styles.divider} />
          <DetailList
            rows={[
              ['Valor atual', money(properties[0].rentPrice)],
              ['Valor sugerido', money(6900)],
              ['Valor por m²', `${money(6900 / properties[0].area)}/m²`],
            ]}
          />
          <div style={{ marginTop: 16 }}>
            <ButtonLink>Gerar relatório</ButtonLink>
          </div>
        </Card>
        <Alert kind="info" title="Valor sugerido">
          Já negociamos muitos imóveis parecidos no mesmo bairro.
        </Alert>
      </div>
    </>
  );
}

export function ListingReportPage() {
  return (
    <>
      <PageIntro
        title="Relatorio de Desempenho"
        subtitle="Imobiliária Demo, em parceria com o QuintoAndar, preparou uma análise de resultado dos útimos 30 dias do seu imóvel"
      />
      <div style={styles.twoColumn}>
        <Card>
          <h2 style={styles.sectionTitle}>Melhore o desempenho do seu imóvel com preço competitivo</h2>
          <DetailList
            rows={[
              ['Valor sugerido', money(6900)],
              ['Valor por m²', `${money(62)}/m²`],
              ['Visualizações', '1.240'],
            ]}
          />
        </Card>
        <PriceTable pricing={properties[0]} />
      </div>
    </>
  );
}

export function PosReportPage() {
  return (
    <>
      <PageIntro
        title="Relatórios de Performance - Pós-Publicação"
        subtitle="Esta visualização apresenta o desempenho dos seus imóveis disponíveis para compra e venda no QuintoAndar."
      />
      <Card style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
        <Tabs
          active="published"
          items={[
            ['published', 'Anúncios publicados'],
            ['visits', 'Visitas'],
            ['offers', 'Propostas'],
            ['ccv', 'Compromissos de compra e venda'],
          ]}
          onChange={() => {}}
        />
        <Alert kind="critical" title="Erro ao carregar informações">
          Ocorreu um erro inesperado ao montar o gráfico. Tente novamente em alguns instantes.
        </Alert>
        <Alert kind="info" title="Sem informações para exibir">
          Ainda não existem dados suficientes para serem mostrados. Em breve essas informações estarão aqui.
        </Alert>
      </Card>
      <div style={{ display: 'grid', gap: 18 }}>
        {posReportSections.map((section) => (
          <Card key={section.id}>
            <h2 style={styles.sectionTitle}>{section.title}</h2>
            <p style={{ ...styles.caption, marginTop: 6 }}>{section.subtitle}</p>
            <div style={{ ...styles.grid, marginTop: 16 }}>
              {section.metrics.map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    background: colors.surfaceAlt,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    padding: 14,
                  }}
                >
                  <span style={styles.caption}>{label}</span>
                  <strong style={{ display: 'block', fontSize: 22, marginTop: 6 }}>{value}</strong>
                  <span style={styles.caption}>Período anterior: dentro da média</span>
                </div>
              ))}
            </div>
            <Alert kind="info" title="Insights sobre este indicador">
              No período analisado, os imóveis de sua carteira tiveram uma conversão de propostas dentro da média.
            </Alert>
          </Card>
        ))}
      </div>
    </>
  );
}

export function ConfirmUserDataPage() {
  return (
    <>
      <PageIntro
        title="Confirme seus dados"
        subtitle="Precisamos confirmar alguns dados para finalizar o seu cadastro"
      />
      <Card>
        <div style={styles.formGrid}>
          <Field label="Nome completo" value="Andre Admin" />
          <Field label="E-mail" value="andre.admin@app2.local" />
          <Field label="Telefone" value="+55 21 98888-0000" />
          <Field label="CRECI" value="12345-F" />
        </div>
        <div style={{ ...styles.row, marginTop: 18 }}>
          <ButtonLink>Confirmar</ButtonLink>
        </div>
        <Alert kind="success" title="Seus dados foram confirmados com sucesso!">
          Confirmação pronta para finalizar o cadastro.
        </Alert>
        <Alert kind="critical" title="Não foi possível confirmar seus dados. Tente novamente.">
          Ocorreu um erro ao enviar os dados. Tente novamente.
        </Alert>
        <Alert kind="critical">
          Não será possível finalizar o cadastro pois a imobiliária não possui a cidade de atuação cadastrada. Entre com contato com o administrador da imobiliária.
        </Alert>
      </Card>
    </>
  );
}

export function CompanyProfilePage() {
  const showOperationAreasError =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('operationAreas') === 'error';

  return (
    <>
      <PageIntro
        actions={<ButtonLink href="/portfolio-manager" variant="secondary">Voltar</ButtonLink>}
        title="Perfil da Imobiliária"
      />
      <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
        <ProfileInfoCard icon="END" title="Endereço">
          <strong style={{ color: colors.muted, display: 'block', marginBottom: 4 }}>
            {companyProfileInfo.addressLine}
          </strong>
          <div>{companyProfileInfo.addressComplement}</div>
          <div>{companyProfileInfo.zipCode}</div>
        </ProfileInfoCard>
        <ProfileInfoCard icon="CI" title="Dados da imobiliária">
          <strong style={{ color: colors.muted, display: 'block', marginBottom: 4 }}>
            {companyProfileInfo.tradeName}
          </strong>
          <div>{companyProfileInfo.companyName}</div>
          <div>{companyProfileInfo.document}</div>
        </ProfileInfoCard>
        <ProfileInfoCard icon="MAP" title="Áreas de atuação">
          {showOperationAreasError ? (
            <Alert kind="critical" title="Desculpe, não foi possível carregar as áreas de atuação">
              Por favor, tente novamente mais tarde.
            </Alert>
          ) : (
            <>
              <p style={{ margin: '0 0 12px' }}>Rio de Janeiro e São Paulo</p>
              <ButtonLink href="/operation-areas?contextType=COMPANY" variant="secondary">
                Gerenciar bairros
              </ButtonLink>
            </>
          )}
        </ProfileInfoCard>
      </div>
    </>
  );
}

export function OperationAreasPage() {
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [activeCity, setActiveCity] = useState('rio');
  const selectedCity = operationCities.find(([id]) => id === activeCity) || operationCities[0];

  const saveAreas = () => {
    setDialog(null);
    setFeedback('success');
    setEditing(false);
  };

  const discardChanges = () => {
    setDialog(null);
    setEditing(false);
  };

  return (
    <>
      <PageIntro
        actions={
          <>
            <ButtonLink href="/company-profile" variant="quiet">Voltar</ButtonLink>
            {editing ? null : (
              <ButtonLink onClick={() => setEditing(true)} variant="secondary">
                Editar áreas selecionadas
              </ButtonLink>
            )}
          </>
        }
        eyebrow="Imobiliária"
        title={`${editing ? 'Edição da área de atuação de' : 'Área de atuação de'} Imobiliária`}
        subtitle={editing ? 'Selecione os bairros no mapa e defina a região em que sua imobiliária atuará.' : null}
      />

      {feedback === 'success' ? (
        <Alert kind="success" title="Área de atuação salva com sucesso.">
          As regiões selecionadas já estão disponíveis para a imobiliária.
        </Alert>
      ) : null}
      {feedback === 'error' ? (
        <Alert kind="critical">
          Não foi possível salvar a área de atuação. Por favor, tente novamente mais tarde
        </Alert>
      ) : null}

      <div style={styles.operationWorkspace}>
        <div style={styles.operationMap}>
          <div style={styles.operationMapToolbar}>
            <strong>Placeholder do mapa</strong>
            <p style={styles.caption}>São Paulo · Rio de Janeiro</p>
            <div style={styles.row}>
              <Tag>{editing ? 'Editando bairros' : 'Bairros selecionados'}</Tag>
              <Tag kind="success">4 bairros</Tag>
            </div>
          </div>
          {[
            ['18%', '58%'],
            ['32%', '44%'],
            ['48%', '63%'],
            ['65%', '38%'],
            ['78%', '55%'],
          ].map(([left, top]) => (
            <span key={`${left}-${top}`} style={{ ...styles.operationMapMarker, left, top }} />
          ))}
        </div>

        <Card>
          <h2 style={styles.sectionTitle}>Bairros selecionados</h2>
          <div style={{ marginTop: 12 }}>
            <Tabs active={activeCity} items={operationCities.map(([id, name]) => [id, name])} onChange={setActiveCity} size="small" />
          </div>
          <div style={{ ...styles.row, justifyContent: 'space-between', marginTop: 16 }}>
            <strong>{selectedCity[1]}</strong>
            {editing ? (
              <ButtonLink variant="quiet">Desmarcar todos os bairros</ButtonLink>
            ) : null}
          </div>
          <ul style={{ ...styles.operationSelectedList, marginTop: 12 }}>
            {selectedCity[2].split(', ').map((region) => (
              <li key={region} style={{ ...styles.card, display: 'flex', justifyContent: 'space-between', listStyle: 'none', padding: 12 }}>
                <span>{region}</span>
                {editing ? <Tag kind="success">Selecionado</Tag> : null}
              </li>
            ))}
          </ul>
          {selectedCity[2] ? null : (
            <Alert title="Nenhum bairro selecionado nessa cidade.">
              Selecione os bairros no mapa para ativar a atuação.
            </Alert>
          )}
        </Card>
      </div>

      {editing ? (
        <div style={{ ...styles.stickyFooter, justifyContent: 'flex-start' }}>
          <ButtonLink onClick={() => setDialog('discard')} variant="quiet">Cancelar</ButtonLink>
          <ButtonLink onClick={() => setDialog('save')}>Salvar área de atuação</ButtonLink>
        </div>
      ) : null}

      <Modal
        actions={
          <>
            <ButtonLink onClick={() => setDialog(null)} variant="secondary">Continuar na página</ButtonLink>
            <ButtonLink onClick={saveAreas}>Salvar</ButtonLink>
          </>
        }
        onClose={() => setDialog(null)}
        open={dialog === 'save'}
        title="Salvar área de atuação"
      >
        Essas áreas também serão <strong>cadastradas automaticamente para todos os novos corretores</strong> e poderão ser alteradas individualmente quando quiser.
      </Modal>

      <Modal
        actions={
          <>
            <ButtonLink onClick={() => setDialog(null)} variant="secondary">Continuar na página</ButtonLink>
            <ButtonLink onClick={discardChanges} variant="danger">Sair sem salvar</ButtonLink>
          </>
        }
        onClose={() => setDialog(null)}
        open={dialog === 'discard'}
        title="Você não salvou as informações"
      >
        Ao sair dessa página, as <strong>seleções feitas serão perdidas</strong>. Para salvar, continue na página e clique no botão <strong>“Salvar área de atuação”</strong>
      </Modal>
    </>
  );
}

export function MembersProfilePage({ mode }) {
  const [invite, setInvite] = useState(null);
  const [profileAction, setProfileAction] = useState(null);
  const [resendMember, setResendMember] = useState(null);
  const [feedback, setFeedback] = useState(null);

  if (mode === 'operation') {
    return (
      <>
        <PageIntro eyebrow="Áreas de operação" title="Áreas de atuação" subtitle="Defina as áreas de atuação da imobiliária" />
        <Alert title="Defina as áreas de atuação da imobiliária">
          Essas áreas serão padrão para os novos corretores cadastrados e poderão ser editadas individualmente a qualquer momento.
        </Alert>
        <Section title="Bairros ativos">
          <DataTable
            columns={[
              { key: 'city', label: 'Cidade', render: (row) => row[0] },
              { key: 'neighborhood', label: 'Bairro', render: (row) => row[1] },
              { key: 'coverage', label: 'Cobertura', render: (row) => row[2] },
            ]}
            rows={operationAreas}
            rowKey="1"
          />
        </Section>
      </>
    );
  }

  if (mode === 'company') {
    return (
      <>
        <PageIntro title="Perfil da Imobiliária" subtitle="Dados cadastrais e contato principal." />
        <Card>
          <div style={styles.formGrid}>
            <Field label="Nome fantasia" value="Imobiliária Demo" />
            <Field label="CNPJ" value="12.345.678/0001-90" />
            <Field label="Telefone" value="+55 21 4002-8922" />
            <Field label="Email" value="contato@imobiliariademo.com" />
          </div>
        </Card>
      </>
    );
  }

  if (mode === 'user') {
    const member = members.find((item) => item.id === 'user-1') || members[0];

    return (
      <>
        <PageIntro
          actions={<ButtonLink href="/members" variant="quiet">Voltar</ButtonLink>}
          title="Informações da conta"
          subtitle={member.name}
        />
        <Card>
          <DetailList
            rows={[
              ['Status', 'Acesso liberado'],
              ['Perfil', member.profile],
              ['Email', member.email],
              ['CRECI', '12345-F'],
            ]}
          />
          <div style={{ ...styles.row, marginTop: 16 }}>
            <ButtonLink href="/members" variant="secondary">Gerenciar Contas</ButtonLink>
            <ButtonLink onClick={() => setProfileAction('release')} variant="secondary">
              Liberar usuário
            </ButtonLink>
            <ButtonLink onClick={() => setProfileAction('deactivate')} variant="danger">
              Desativar Conta
            </ButtonLink>
          </div>
          {feedback === 'release-success' ? (
            <Alert kind="success" title="Usuário liberado com sucesso.">
              O acesso desse usuário foi atualizado.
            </Alert>
          ) : null}
          {feedback === 'release-error' ? (
            <Alert kind="critical" title="Houve um erro ao liberar o usuário, tente novamente.">
              Tente novamente dentro de alguns instantes.
            </Alert>
          ) : null}
        </Card>

        <Modal
          actions={
            <>
              <ButtonLink onClick={() => setProfileAction(null)} variant="secondary">Cancelar</ButtonLink>
              <ButtonLink onClick={() => {
                setProfileAction(null);
                setFeedback('release-success');
              }}>
                Liberar usuário
              </ButtonLink>
            </>
          }
          onClose={() => setProfileAction(null)}
          open={profileAction === 'release'}
          title="Liberar usuário"
        >
          <p style={{ marginTop: 0 }}>
            Identificamos que o usuário {member.name} pertence à sua imobiliária. Para liberar o acesso ao Marketplace, precisamos primeiro confirmar o perfil desse usuário.
          </p>
          <Field
            as="select"
            label="Perfil"
            options={['Administrador', 'Associado', 'Corretor', 'Membro']}
            value="Administrador"
          />
        </Modal>

        <Modal
          actions={<ButtonLink onClick={() => setProfileAction(null)}>Entendi</ButtonLink>}
          onClose={() => setProfileAction(null)}
          open={profileAction === 'deactivate'}
          title="Desativar Conta"
        >
          <Alert kind="critical" title="Desativar conta">
            Esta ação não pode ser feita na plataforma. Para desativar essa conta entre em contato com o suporte especializado QuintoAndar.
          </Alert>
        </Modal>
      </>
    );
  }

  const confirmInvite = () => {
    setResendMember(null);
    setFeedback('resend-success');
  };

  const submitNewInvite = () => {
    setInvite('success');
  };

  const invitationPreview = resendMember || {
    email: 'novousuario@email.com',
    name: 'Novo usuário',
    phone: '(11) 00000-0000',
    profile: 'Corretor',
  };

  return (
    <>
      <PageIntro
        actions={<ButtonLink href="/company-profile" variant="quiet">Voltar</ButtonLink>}
        title="Gerenciar usuários"
      />
      <Card>
        <div style={{ ...styles.twoColumn, alignItems: 'center' }}>
          <div>
            <h2 style={styles.sectionTitle}>Adicione novos usuários a sua conta</h2>
            <p style={{ ...styles.subtitle, fontSize: 15, marginTop: 8 }}>
              Convide pessoas de sua confiança para auxiliar na gestão dos seus imóveis.
              <br />
              Os novos usuários terão acesso a <strong>visualização dos imóveis, agenda de visitas e gestão de propostas.</strong>
            </p>
          </div>
          <div style={{ justifySelf: 'end' }}>
            <ButtonLink onClick={() => setInvite('form')}>Adicionar usuários</ButtonLink>
          </div>
        </div>
      </Card>
      <Section title="Convide colaboradores para auxiliar na gestão dos imóveis e ter acesso às ferramentas do QuintoAndar">
        <div style={styles.grid}>
          <Alert kind="info">
            <strong>Perfil de Corretor:</strong> Para gestão dos imóveis da sua carteira e acesso ao app de corretores
          </Alert>
          <Alert kind="info">
            <strong>Perfil de Administrador:</strong> Para gestão de todos os imóveis da imobiliária, agendamento de visitas, propostas e acessos.
          </Alert>
        </div>
      </Section>
      {feedback === 'resend-success' ? (
        <div
          role="status"
          style={{
            background: colors.success,
            borderRadius: 6,
            bottom: 20,
            boxShadow: '0 10px 28px rgba(16, 24, 40, 0.18)',
            color: '#ffffff',
            fontWeight: 800,
            left: 20,
            padding: '14px 18px',
            position: 'fixed',
            zIndex: 60,
          }}
        >
          Convite enviado com sucesso.
        </div>
      ) : null}
      <DataTable
        columns={[
          { key: 'name', label: 'Nome' },
          { key: 'email', label: 'E-mail' },
          { key: 'profile', label: 'Perfil', render: (row) => <Tag>{row.profile}</Tag> },
          {
            key: 'status',
            label: 'Status',
            render: (row) => (
              <div>
                <Tag>{row.status === 'Convite enviado' ? 'Convidado' : row.status}</Tag>
                {row.status === 'Convite enviado' ? (
                  <p style={{ ...styles.caption, marginTop: 6 }}>Convidado em 13/07/2026</p>
                ) : null}
              </div>
            ),
          },
          {
            key: 'actions',
            label: 'Ações',
            render: (row) => (
              <div style={styles.row}>
                <ButtonLink href={`/user-profile/${row.id}`} variant="secondary">Gerenciar Contas</ButtonLink>
                {row.status === 'Convite enviado' ? (
                  <ButtonLink onClick={() => setResendMember(row)} variant="quiet">Reenviar convite</ButtonLink>
                ) : null}
              </div>
            ),
          },
        ]}
        rows={members}
      />
      <Drawer
        onClose={() => setInvite(null)}
        open={invite === 'form' || invite === 'success'}
        title={invite === 'success' ? 'Convite enviado' : 'Adicionar novo usuário'}
      >
        {invite === 'success' ? (
          <FeedbackBlock
            action={<ButtonLink onClick={() => setInvite(null)} variant="quiet">Voltar</ButtonLink>}
            kind="positive"
            title="Convite enviado"
            subtitle="O novo usuário receberá um e-mail com o convite para ativar a conta e acessar as informações da sua Carteira de Imóveis."
          />
        ) : (
          <>
            <p style={{ ...styles.subtitle, fontSize: 15 }}>
              Preencha as informações para enviar o convite de acesso para o novo usuário
            </p>
            <Field
              as="select"
              label="Selecione o perfil de usuário"
              options={['Administrador', 'Convidado', 'Corretor']}
              value="Corretor"
            />
            <Alert kind="info" title="Perfil corretor">
              Terá acesso à visualização dos imóveis captados por ele, à calculadora de preços e ao aplicativo de corretores do QuintoAndar.
            </Alert>
            <div style={styles.formGrid}>
              <Field label="Nome do usuário" placeholder="Nome completo do novo usuário" value="" />
              <Field label="E-mail" placeholder="novousuario@email.com" value="" />
              <Field label="Telefone" placeholder="(00) 0 0000-0000" value="" />
            </div>
            <div style={styles.stickyFooter}>
              <ButtonLink onClick={submitNewInvite}>Adicionar usuário</ButtonLink>
            </div>
          </>
        )}
      </Drawer>
      <Modal
        actions={
          <>
            <ButtonLink onClick={() => {
              setResendMember(null);
            }} variant="secondary">
              Cancelar
            </ButtonLink>
            <ButtonLink onClick={confirmInvite}>Confirmar envio</ButtonLink>
          </>
        }
        onClose={() => {
          setResendMember(null);
        }}
        open={!!resendMember}
        title="Enviar convite"
      >
        <p style={{ marginTop: 0 }}>
          Ao enviar o convite, você autoriza a pessoa abaixo a ter acesso à Central da Imobiliária:
        </p>
        <DetailList
          rows={[
            ['E-mail', invitationPreview.email],
            ['Telefone', invitationPreview.phone || '(11) 00000-0000'],
            ['Nome', invitationPreview.name],
            ['Perfil', invitationPreview.profile],
          ]}
        />
      </Modal>
    </>
  );
}

MembersProfilePage.propTypes = {
  mode: PropTypes.string,
};

export function IntegrationAuthPage({ mode }) {
  const [configModal, setConfigModal] = useState(false);

  if (mode === 'unsupported') {
    return (
      <>
        <PageIntro title="Conflito de perfil" subtitle="Perfil não suportado para esta operação." />
        <FeedbackBlock
          kind="critical"
          title="Esse usuário não tem acesso"
          subtitle="Tente acessar com outra conta. Se precisar, entre em contato conosco."
          action={
            <div style={styles.row}>
              <ButtonLink>Entrar em contato</ButtonLink>
              <ButtonLink variant="secondary">Sair da conta</ButtonLink>
            </div>
          }
        />
        <Section title="Acesso disponível">
          <div style={styles.row}>
            <Tag>à Carteira de Imóveis</Tag>
            <Tag>à Multicarteira</Tag>
            <Tag>à Central da imobiliária</Tag>
          </div>
        </Section>
      </>
    );
  }

  if (mode === 'conflict') {
    return (
      <>
        <PageIntro title="Conflito de perfil" subtitle="Escolha qual perfil deseja utilizar." />
        <div style={styles.twoColumn}>
          <Card><h2 style={styles.sectionTitle}>Perfil novo</h2><p style={styles.caption}>Corretor de Visitas</p></Card>
          <Card><h2 style={styles.sectionTitle}>Perfil atual</h2><p style={styles.caption}>Perfil administrador</p></Card>
        </div>
      </>
    );
  }

  if (mode === 'integration') {
    return (
      <>
        <PageIntro title="Integração com a Imovelweb" subtitle="Prévia da Integração e publicação dos imóveis elegíveis." />
        <div style={styles.twoColumn}>
          <Card>
            <h2 style={styles.sectionTitle}>Prévia da Integração</h2>
            <p style={styles.caption}>Confira filtros, imóveis elegíveis e campos enviados para a Imovelweb.</p>
            <div style={{ ...styles.row, marginTop: 16 }}>
              <ButtonLink onClick={() => setConfigModal(true)}>Editar configurações</ButtonLink>
              <ButtonLink variant="secondary">Ver imóveis</ButtonLink>
            </div>
          </Card>
          <Card>
            <h2 style={styles.sectionTitle}>Resumo dos parâmetros</h2>
            <DetailList rows={integrationParameters} />
            <p style={{ ...styles.caption, marginTop: 12 }}>
              Autorizo o QuintoAndar a processar e publicar imóveis dentro dos parâmetros estabelecidos.
            </p>
          </Card>
          <Card>
            <h2 style={styles.sectionTitle}>Integração ativa</h2>
            <Tag kind="success">Ativa</Tag>
            <p style={{ ...styles.caption, marginTop: 8 }}>Integração</p>
          </Card>
        </div>
        <Section title="Possível despublicação de anúncios">
          <Alert kind="warning" title="Possível despublicação de anúncios">
            Os filtros selecionados são diferentes dos atualmente aplicados, e imóveis fora dos novos parâmetros serão desativados.
          </Alert>
        </Section>
        <Modal
          actions={
            <>
              <ButtonLink onClick={() => setConfigModal(false)} variant="secondary">Voltar</ButtonLink>
              <ButtonLink onClick={() => setConfigModal(false)} variant="secondary">Confirmar seleção</ButtonLink>
              <ButtonLink onClick={() => setConfigModal(false)}>Salvar configurações</ButtonLink>
            </>
          }
          onClose={() => setConfigModal(false)}
          open={configModal}
          title="Prévia da Integração"
        >
          <p style={{ ...styles.caption, marginBottom: 14 }}>
            Os imóveis serão avaliados de acordo com as regras do Marketplace QuintoAndar, e o processamento não garante a publicação.
          </p>
          <div style={styles.formGrid}>
            <div>
              <span style={styles.label}>Busque por bairros específicos</span>
              <SearchBox placeholder="Busque por bairros específicos" />
            </div>
            <Field as="select" label="Região do imóvel" options={['Todos os bairros', 'Leblon', 'Ipanema', 'Botafogo']} value="Todos os bairros" />
            <CurrencyField helper="Mínimo permitido: R$ 2.500" label="Mínimo" value="2.500" />
            <CurrencyField helper="Máximo permitido: R$ 18.000" label="Máximo" value="18.000" />
          </div>
          <div style={{ marginTop: 16 }}>
            <h3 style={{ fontSize: 16, margin: '0 0 10px' }}>Preço do imóvel</h3>
            <div style={styles.row}>
              <Tag kind="neutral">1 bairro selecionado</Tag>
              <Tag kind="neutral">2 bairros selecionados</Tag>
            </div>
          </div>
          <Alert kind="critical" title="Menor que o mínimo permitido">
            Maior que o máximo permitido
          </Alert>
          <Alert kind="critical" title="Erro ao salvar configurações">
            Ocorreu um erro ao salvar suas configurações. Tente novamente mais tarde.
          </Alert>
          <Alert kind="critical" title="Erro ao exibir filtros">
            Ocorreu um erro ao exibir os filtros de integração. Aguarde um pouco e tente novamente mais tarde.
          </Alert>
        </Modal>
      </>
    );
  }

  return (
    <>
      <PageIntro title="Acesso e senha" subtitle="Login de terceiros, primeira senha e recuperação de acesso." />
      <Card>
        <div style={styles.formGrid}>
          <Field label="Email" value="andre.admin@app2.local" />
          <Field label="Código de acesso" value="123456" />
          <Field label="Nova senha" type="password" value="app2-demo" />
        </div>
      </Card>
    </>
  );
}

IntegrationAuthPage.propTypes = {
  mode: PropTypes.string,
};

export function AuthAccessPage({ mode }) {
  const isFirstAccess = mode === 'first-access';
  const isFirstLogin = mode === 'first-login';
  const isReset = mode === 'reset-password';
  const isNewPassword = mode === 'new-password';
  const isForgotPassword = mode === 'forgot-password';
  const title = isFirstAccess || isFirstLogin
    ? 'Primeiro acesso'
    : isNewPassword
      ? 'Cadastrar nova senha'
      : 'Redefinir senha';

  return (
    <>
      <PageIntro
        title={title}
        subtitle={isFirstAccess || isFirstLogin ? 'crie sua senha e realize o primeiro acesso' : 'Informe seu e-mail e cadastre uma nova senha.'}
      />
      <div style={styles.twoColumn}>
        <Card>
          <h2 style={styles.sectionTitle}>{isFirstAccess ? 'Primeiro acesso à sua conta' : title}</h2>
          <div style={styles.formGrid}>
            <Field label="E-mail" value="andre.admin@app2.local" />
            {isForgotPassword ? null : <Field label="Nova senha" type="password" value="app2-demo" />}
            {isForgotPassword ? null : <Field label="Confirmar senha" type="password" value="app2-demo" />}
          </div>
          <div style={{ ...styles.row, marginTop: 16 }}>
            <ButtonLink>{isFirstAccess ? 'Continuar' : isForgotPassword ? 'Redefinir senha' : 'Cadastrar nova senha'}</ButtonLink>
          </div>
        </Card>
        <div style={{ display: 'grid', gap: 14 }}>
          <Alert kind="warning" title="Seu link para criação de senha expirou, te enviamos um novo">
            Verifique o seu e-mail andre.admin@app2.local. Caso não tenha recebido, você pode solicitar o reenvio.
          </Alert>
          <Alert kind="critical" title="O link de acesso não é válido">
            Confira o link correto em seu e-mail. Caso tenha alguma dúvida, suporte@rede.quintoandar.com.br ou converse com a gente pelo WhatsApp (11) 4040-2223 de segunda à quinta-feira, das 08h00 às 19h00, ou sexta-feira, das 08h00 às 18h00.
          </Alert>
          <Alert kind="success" title="Pronto, você já pode acessar sua conta!">
            Tudo certo com o seu primeiro acesso, agora é só começar a usar.
            <div style={{ marginTop: 10 }}>
              <ButtonLink href="/portfolio-manager" variant="secondary">Acessar minha conta</ButtonLink>
            </div>
          </Alert>
        </div>
      </div>
    </>
  );
}

AuthAccessPage.propTypes = {
  mode: PropTypes.string,
};

export function LeadDetailPage({ params }) {
  const context = params.businessContext === 'RENT' ? 'RENT' : 'SALE';
  const lead = leadEntities[context];
  const [modal, setModal] = useState(false);

  return (
    <>
      <PageIntro eyebrow="Detalhe do lead" title={lead.title} subtitle="Página de acompanhamento recuperada" />
      <div style={styles.twoColumn}>
        <Card>
          <Tag>{lead.listingStatus}</Tag>
          <h2 style={{ ...styles.sectionTitle, marginTop: 14 }}>{lead.title}</h2>
          <p style={{ ...styles.caption, marginTop: 8 }}>{lead.headline}</p>
          <div style={{ marginTop: 16 }}>
            <ButtonLink onClick={() => setModal(true)}>{lead.primaryAction}</ButtonLink>
          </div>
        </Card>
        <Card>
          <h2 style={styles.sectionTitle}>O anúncio está sendo preparado</h2>
          <p style={{ ...styles.caption, marginTop: 8 }}>{lead.processingText}</p>
          <hr style={styles.divider} />
          <strong>Sessão de fotos</strong>
          <p style={styles.caption}>16/07/2026 às 10:30 com Renata Lins.</p>
        </Card>
      </div>
      <Modal
        actions={
          <>
            <ButtonLink onClick={() => setModal(false)} variant="secondary">Cancelar</ButtonLink>
            <ButtonLink onClick={() => setModal(false)}>{lead.primaryAction}</ButtonLink>
          </>
        }
        onClose={() => setModal(false)}
        open={modal}
        title={lead.modalTitle}
      >
        {lead.modalBody}
      </Modal>
    </>
  );
}

LeadDetailPage.propTypes = {
  params: PropTypes.object,
};

export function NotFoundPage({ pathname }) {
  const [title, subtitle] = useMemo(() => titleForPath(pathname), [pathname]);
  return (
    <>
      <PageIntro title={title} subtitle={subtitle} />
      <FeedbackBlock title="Ops! Parece que você não tem permissão para acessar essa página" subtitle="Em caso de dúvida, entre em contato conosco." action={<ButtonLink>Entrar em contato</ButtonLink>} />
    </>
  );
}

NotFoundPage.propTypes = {
  pathname: PropTypes.string.isRequired,
};
